import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import {
    getAllRmsByInterests,
    getAllRmsByPopularity,
    getCompanyById,
    getRmsByInterests,
    getRmsByPopularity
} from "../../api/company";
import {CompanyRmExtendedInterface, CompanyRmI} from "../../db/models/company/rm";
import {useTranslation} from "react-i18next";
import noCompanyLogo from "../../assets/noCompanyLogo.svg";
import {selectCv, selectUser} from "../../slices/profile";
import {ROUTES} from "../../db/models/const";
import {getContentsByRmId} from "../../api/content";
import {VideoFrame} from "../../components/Common/VideoFrame/main";
import {Steps} from "intro.js-react";
import {Cookies} from "react-cookie-consent";
import {Alert, Button} from "react-bootstrap";
import {closeDialog, openDialog} from "../../slices/dialog";

interface Props {
    topRef?: HTMLDivElement;
}

export const StudentFeed: React.FC<Props> = ({topRef}) => {
    const history = useHistory();
    const [interestLoading, setInterestLoading] = useState(true);
    const [popularLoading, setPopularLoading] = useState(true);
    const user = useSelector(selectUser);
    const cv = useSelector(selectCv);
    const interestScopes = user?.interestScopes || [];
    const [popularRms, setPopularRms] = useState<CompanyRmExtendedInterface[]>([]);
    const dispatch = useDispatch();
    const [interestRms, setInterestRms] = useState<CompanyRmExtendedInterface[]>([]);
    const {t} = useTranslation();
    const tutorialSteps = [
        {
            element: ".step1",
            intro: (
                <div>
                    <h3>{t("student_feed_tutorial_title")}</h3>
                    <p>{t("student_feed_tutorial_p")}</p>
                </div>
            ),
            position: "bottom",
        },
    ];
    const [enableTutorial, setEnableTutorial] = useState<boolean>(false);

    const handleFeedTutorial = () => {
        setEnableTutorial(true);
    };

    const onStepsClose = () => {
        Cookies.set("feedTutorialDone", true, {expires: 1000});
        setEnableTutorial(false);
    };

    // useEffect(() => {
    // 	if (user === undefined || cv === undefined) return;

    // 	if (!user.name || !user.interestScopes || !cv?.softSkills) {
    // 		dispatch(
    // 			openDialog({
    // 				Content: StudentOnboarding,
    // 				title: "",
    // 				props: { handleFeedTutorial },
    // 				closable: false,
    // 			})
    // 		);
    // 		return;
    // 	}
    // }, [user, cv]);

    // get rms by interests and add related company and content
    const asyncInterestRms = async () => {
        let rms: CompanyRmExtendedInterface[] = [];

        if (!user?.demo) rms = await getRmsByInterests(interestScopes);
        else rms = await getAllRmsByInterests(interestScopes);

        setInterestLoading(false);

        const rmsProms = rms.map(async (rm) => {
            return {
                ...rm,
                contents: await getContentsByRmId(rm.id),
                company: await getCompanyById(rm.companyId),
            };
        });
        rms = await Promise.all(rmsProms);

        // rms = await addCompanyToRms(rms);
        setInterestRms(rms);
    };

    // get all rms not in demo
    const asyncPopularRms = async () => {
        let rms: CompanyRmExtendedInterface[] = [];

        if (!user?.demo) rms = await getRmsByPopularity();
        else rms = await getAllRmsByPopularity();

        setPopularLoading(false);

        const postPromises = rms.map(async (rm) => {
            return {
                ...rm,
                contents: await getContentsByRmId(rm.id),
                company: await getCompanyById(rm.companyId),
            };
        });
        rms = await Promise.all(postPromises);

        // const extRms: CompanyRmI[] = await addCompanyToRms(rms);
        setPopularRms(rms);
    };

    const onRmClick = (extRm: CompanyRmI) => {
        if (!extRm) return;

        if (extRm?.coming) history.push(ROUTES.STUDENT_RM_COMING.replace(":rmId", extRm.id));
        else history.push(ROUTES.STUDENT_RM.replace(":rmId", extRm.id));
    };

    const openFeedIntroDialog = () => {
        const Content = () => {
            return (<div>
                <p>{t("student_feed_intro_description")}</p>
                <Button className={"mt-5 w-100 oval-borders"} size={"lg"}
                        variant={'primary'} onClick={() => dispatch(closeDialog())}>
                    {t("student_feed_intro_button")}</Button>
            </div>)
        }
        dispatch(
            openDialog({
                Content: Content,
                title: t("student_feed_intro_title"),
                props: {},
                closable: false,
                narrow: true,
            })
        );
    }

    useEffect(() => {
        if (!Cookies.get("feed_introduction")) {
            openFeedIntroDialog()
            Cookies.set("feed_introduction", true, {expires: 1000});
        }
        asyncPopularRms();
    }, []);

    useEffect(() => {
        if (interestScopes?.length === 0) return;

        asyncInterestRms();
    }, [interestScopes]);

    return (
        <div className="studentFeed">
            {user?.limitations?.hideFeed &&
                <Alert variant="warning">Non hai i permessi per visualizzare la lista di percosi</Alert>}
            {!(user?.limitations?.hideFeed) && (
                <>
                    <Steps
                        enabled={enableTutorial}
                        steps={tutorialSteps}
                        initialStep={0}
                        onExit={onStepsClose}
                        onComplete={onStepsClose}
                        options={{
                            hideNext: false,
                            skipLabel: "Skip",
                            tooltipClass: "customIntroJSTooltip",
                        }}
                    />
                    {!interestLoading && interestRms.length > 0 && (
                        <>
                            <h2 className="studentFeed__title">{t("student_feed_suggested_title")}</h2>
                            <p className="studentFeed__subtitle">{t("student_feed_suggested_subtitle")}</p>
                            <div className="studentFeed__rmGrid">
                                {interestRms.map((rm) => (
                                    <div className="card studentFeed__rm" key={rm.id} onClick={() => onRmClick(rm)}>
                                        {rm.contents?.[0] &&
                                            <VideoFrame width="160px" height="160px" video={rm.contents?.[0]}/>}
                                        <div className="rm__info">
                                            <div className="rm__header">
                                                <div
                                                    className="rm__companyLogo"
                                                    style={{
                                                        backgroundImage: `url(${rm?.company?.logoUrl || noCompanyLogo})`,
                                                    }}
                                                />
                                                <div className="rm__titles">
                                                    <div className="rm__rmName">{rm.name}</div>
                                                    <div className="rm__companyName">{rm.company?.name}</div>
                                                </div>
                                            </div>
                                            <div dangerouslySetInnerHTML={{__html: rm.mission ?? ""}} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                    {/* {interestLoading && (
					<div className="loader">
						<ClipLoader size={20} color={"#7400b8"} loading={true} />
					</div>
				)} */}

                    {!popularLoading && popularRms.length > 0 && (
                        <>
                            <h2 className="studentFeed__title mt20">{t("student_feed_all_title")}</h2>
                            <p className="studentFeed__subtitle">{t("student_feed_all_subtitle")}</p>
                            <div className="studentFeed__rmGrid">
                                {popularRms.map((rm, rmIndex) => (
                                    <div className="card studentFeed__rm" key={rm.id} onClick={() => onRmClick(rm)}>
                                        {rm.contents?.[0] &&
                                            <VideoFrame width="160px" height="160px" video={rm.contents?.[0]}/>}
                                        <div className="rm__info">
                                            <div className="rm__header">
                                                <div
                                                    className="rm__companyLogo"
                                                    style={{
                                                        backgroundImage: `url(${rm?.company?.logoUrl || noCompanyLogo})`,
                                                    }}
                                                />
                                                <div className="rm__titles">
                                                    <div className="rm__rmName">{rm.name}</div>
                                                    <div className="rm__companyName">{rm.company?.name}</div>
                                                </div>
                                            </div>
                                            <div dangerouslySetInnerHTML={{__html: rm.mission ?? ""}} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                    {/* {popularLoading && (
					<div className="loader">
						<ClipLoader size={20} color={"#7400b8"} loading={true} />
					</div>
				)} */}

                    {/* {showSteps && (
					<>
						<h2 className="studentFeed__title" style={{ marginTop: "20px" }}>
							{t("student_feed_steps_title")}
						</h2>
						<div className="studentFeed__rmGrid">
							{stepsRms.map((rm) => (
								<div
									className="card studentFeed__rm"
									key={rm.id}
									onClick={() => onRmClick(rm)}
								>
									<div
										className="rm__companyLogo"
										style={{
											backgroundImage: `url(${rm?.company?.logoUrl || noCompanyLogo})`
										}}
									></div>
									<div className="rm__info">
										<div className="rm__rmName">{rm.name}</div>
										<div className="rm__companyName">{rm.company?.name}</div>
										{rm?.tag != null && (
											<div className="rm__tag">
												{"#" + t(professionScopes[rm?.tag]?.label) || ""}
											</div>
										)}
										<div dangerouslySetInnerHTML={{__html: rm.mission ?? ""}} />
									</div>
								</div>
							))}
						</div>
					</>
				)} */}
                </>
            )}
        </div>
    );
};
